import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Spin, Table } from "antd";
import { Spinner } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import backdrop from "@mui/material/Backdrop";
import { toast } from "react-toastify";
import { FiDownload, FiUpload, FiTrendingUp, FiTrendingDown, FiClock, FiUser } from "react-icons/fi";
import SidebarNav from "../../sidebar";
import FilterPanel from "../../commoncomponent/FilterPanel";
import {
  getWithdrawalFeesApi,
  exportWithdrawalFeesCsvApi,
  uploadWithdrawalExpenseApi,
  listWithdrawalExpenseUploadsApi,
} from "../../../../service/api";
import { formatInr, formatNumber, KpiCard, saveBlobAsFile } from "../shared";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 420,
  background: "white",
  borderRadius: 10,
  boxShadow: 24,
  padding: 20,
  outline: "none",
};

const WithdrawalFees = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [form, setForm] = useState({
    year: currentYear,
    month: new Date().getMonth() + 1,
    coin: "",
    totalNetworkFeeUsd: "",
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [feesRes, uploadsRes] = await Promise.all([
        getWithdrawalFeesApi({ year }),
        listWithdrawalExpenseUploadsApi({ year, page: 1, sizePerPage: 50 }),
      ]);
      if (feesRes?.status) setData(feesRes.data);
      if (uploadsRes?.status) setUploads(uploadsRes.data?.result || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportWithdrawalFeesCsvApi({ year });
      saveBlobAsFile(res, `withdrawal-fees-${year}.csv`);
    } catch (e) {
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.coin || form.totalNetworkFeeUsd === "") {
      toast.error("Coin and total network fee are required", { toastId: 'withdrawal-fees-validation-error' });
      return;
    }
    setUploadSubmitting(true);
    try {
      const res = await uploadWithdrawalExpenseApi({
        year: parseInt(form.year),
        month: parseInt(form.month),
        coin: form.coin.trim().toUpperCase(),
        totalNetworkFeeUsd: parseFloat(form.totalNetworkFeeUsd),
        notes: form.notes || null,
      });
      if (res?.status) {
        toast.success("Expense uploaded", { toastId: 'withdrawal-fees-upload-success' });
        setUploadOpen(false);
        setForm({ ...form, coin: "", totalNetworkFeeUsd: "", notes: "" });
        load();
      } else {
        toast.error(res?.message || "Upload failed", { toastId: 'withdrawal-fees-upload-error' });
      }
    } catch (err) {
      toast.error(err?.data?.message || "Upload failed", { toastId: 'withdrawal-fees-upload-error' });
    } finally {
      setUploadSubmitting(false);
    }
  };

  const barChart = useMemo(() => {
    const rows = data?.rows || [];
    return {
      options: {
        chart: { type: "bar", toolbar: { show: false } },
        plotOptions: { bar: { columnWidth: "50%", borderRadius: 6, borderRadiusApplication: "end" } },
        xaxis: {
          categories: rows.map((r) => r.label),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { fontSize: "12px", colors: "#8a94a6" } },
        },
        yaxis: { labels: { formatter: (v) => formatInr(v), style: { fontSize: "11px", colors: "#8a94a6" } } },
        legend: { position: "top", horizontalAlign: "right", fontSize: "13px" },
        colors: ["#28a745", "#dc3545"],
        tooltip: { y: { formatter: (v) => formatInr(v) } },
        dataLabels: { enabled: false },
        grid: { borderColor: "#f3f4f6", strokeDashArray: 3 },
        fill: { opacity: 0.9 },
      },
      series: [
        { name: "Gas Income", data: rows.map((r) => Number(r.income || 0)) },
        { name: "Gas Expense", data: rows.map((r) => Number(r.expense || 0)) },
      ],
    };
  }, [data]);

  const tableColumns = [
    {
      title: "Month", dataIndex: "label", key: "label",
      render: (v) => <span style={{ fontWeight: 600, color: "#374151" }}>{v}</span>,
    },
    {
      title: "Withdrawals", key: "count", align: "right",
      render: (_, r) => <span style={{ fontWeight: 600, color: "#1b5a90" }}>{formatNumber(r.count)}</span>,
    },
    {
      title: "Gas Income", key: "income", align: "right",
      render: (_, r) => <span style={{ color: "#28a745", fontWeight: 600 }}>{formatInr(r.income)}</span>,
    },
    {
      title: "Gas Expense", key: "expense", align: "right",
      render: (_, r) => <span style={{ color: "#dc3545", fontWeight: 600 }}>{formatInr(r.expense)}</span>,
    },
    {
      title: "Margin", key: "margin", align: "right",
      render: (_, r) => {
        const v = Number(r.margin || 0);
        const pos = v >= 0;
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 700, color: pos ? "#28a745" : "#dc3545" }}>
            {pos ? <FiTrendingUp size={13} /> : <FiTrendingDown size={13} />}
            {formatInr(v)}
          </span>
        );
      },
    },
  ];

  const COIN_COLORS = { BTC: "#f7931a", ETH: "#627eea", USDT: "#26a17b", BNB: "#f3ba2f" };

  const uploadsColumns = [
    {
      title: "Period", key: "period",
      render: (_, r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eef4fb", color: "#1b5a90", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
          <FiClock size={11} />
          {String(r.month).padStart(2, "0")}/{r.year}
        </span>
      ),
    },
    {
      title: "Coin", dataIndex: "coin", key: "coin",
      render: (v) => {
        const color = COIN_COLORS[v] || "#6f42c1";
        return (
          <span style={{ display: "inline-block", background: `${color}18`, color, border: `1px solid ${color}40`, borderRadius: 6, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
            {v || "-"}
          </span>
        );
      },
    },
    {
      title: "Network Fee (USD→INR)", key: "totalNetworkFeeUsd", align: "right",
      render: (_, r) => <span style={{ color: "#dc3545", fontWeight: 600 }}>{formatInr(r.totalNetworkFeeUsd)}</span>,
    },
    {
      title: "Uploaded By", key: "uploadedByEmail",
      render: (_, r) => r.uploadedByEmail ? (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eef4fb", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <FiUser size={11} color="#1b5a90" />
          </div>
          <span style={{ fontSize: 12, color: "#374151" }}>{r.uploadedByEmail}</span>
        </div>
      ) : "-",
    },
    {
      title: "Notes", dataIndex: "notes", key: "notes",
      render: (v) => v ? <span style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>{v}</span> : <span style={{ color: "#d1d5db" }}>—</span>,
    },
    {
      title: "Uploaded At", key: "createdAt",
      render: (_, r) => r.createdAt ? (
        <span style={{ fontSize: 12, color: "#6b7280" }}>{new Date(r.createdAt).toLocaleString()}</span>
      ) : "-",
    },
  ];

  const totals = data?.totals || { income: 0, expense: 0, margin: 0, count: 0 };

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header py-3 mb-2">
            <div className="row align-items-center">
              <div className="col-sm-12">
                <h3 className="page-title mb-1">Crypto Withdrawal Fees</h3>
                <ul className="breadcrumb mb-0" style={{ fontSize: 13 }}>
                  <li className="breadcrumb-item">
                    <Link to="/admin" className="text-decoration-none">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Finance Dashboard / Withdrawal Fees</li>
                </ul>
                {data?.usdtInrRate && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#eef4fb", border: "1px solid #c8dff5", borderRadius: 8, padding: "9px 16px", marginTop: 10, fontSize: 13, color: "#2d7dd2" }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>ℹ</span>
                    <span>Live rate: <strong>1 USDT = ₹{Number(data.usdtInrRate).toFixed(2)}</strong> — gas expense (USD) converted to INR at this rate</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="list-layout">
            <div className="list-layout__main">
              <div className="row g-3 mb-3">
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="↓" label={`Gas Income ${year}`} value={formatInr(totals.income)} color="#28a745" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="↑" label={`Gas Expense ${year}`} value={formatInr(totals.expense)} color="#dc3545" />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard
                    icon="∑"
                    label="Net Withdrawal Margin"
                    value={formatInr(totals.margin)}
                    color={Number(totals.margin) >= 0 ? "#28a745" : "#dc3545"}
                    highlight
                  />
                </div>
                <div className="col-xl-3 col-sm-6 col-12">
                  <KpiCard icon="#" label="Withdrawals Count" value={formatNumber(totals.count)} color="#1b5a90" />
                </div>
              </div>

              {/* Chart card */}
              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>Income vs Expense</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 3 }}>Monthly comparison for {year}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#eaf7ee", border: "1px solid #28a74530", borderRadius: 8, padding: "6px 12px" }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#28a745", flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: "#28a745", fontWeight: 600 }}>Income: {formatInr(totals.income)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, background: "#fdf0f1", border: "1px solid #dc354530", borderRadius: 8, padding: "6px 12px" }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#dc3545", flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: "#dc3545", fontWeight: 600 }}>Expense: {formatInr(totals.expense)}</span>
                      </div>
                    </div>
                  </div>
                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><Spin /></div>
                  ) : (
                    <ReactApexChart options={barChart.options} series={barChart.series} type="bar" height={320} />
                  )}
                </div>
              </div>

              {/* Monthly Detail table */}
              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>Monthly Detail</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 3 }}>{data?.rows?.length || 0} months in {year}</div>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <button
                        onClick={() => setUploadOpen(true)}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", background: "#fff", border: "1.5px solid #1b5a90", borderRadius: 10, color: "#1b5a90", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                      >
                        <FiUpload size={14} /> Upload Expense
                      </button>
                      <button
                        onClick={handleExport}
                        disabled={exporting}
                        style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", background: "#1b5a90", border: "none", borderRadius: 10, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", opacity: exporting ? 0.7 : 1 }}
                      >
                        <FiDownload size={14} />
                        {exporting ? <Spinner size="sm" /> : "Export CSV"}
                      </button>
                    </div>
                  </div>
                  <div className="table-responsive">
                    {loading ? (
                      <div className="his-spin"><Spin size="large" /></div>
                    ) : (
                      <Table
                        pagination={false}
                        style={{ overflowX: "auto" }}
                        columns={tableColumns}
                        dataSource={data?.rows || []}
                        rowKey={(r) => `${r.year}-${r.month}`}
                        locale={{ emptyText: <div style={{ padding: "32px 0", color: "#888" }}>No data for {year}</div> }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Past Uploads table */}
              <div className="card" style={{ borderRadius: 12, border: "1px solid #e9ecef", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
                <div className="card-body">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div>
                      <h6 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a2e", margin: 0 }}>Past Uploads</h6>
                      <div style={{ fontSize: 12, color: "#8a94a6", marginTop: 3 }}>{uploads.length} record{uploads.length === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      pagination={false}
                      style={{ overflowX: "auto" }}
                      columns={uploadsColumns}
                      dataSource={uploads}
                      rowKey={(r) => r._id}
                      locale={{ emptyText: <div style={{ padding: "32px 0", color: "#888" }}>No uploads yet for {year}</div> }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="list-layout__filters">
              <FilterPanel count={data?.rows?.length || 0} onClear={() => setYear(currentYear)}>
                <FilterPanel.Field label="Year">
                  <input
                    type="number"
                    min={2020}
                    max={2100}
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value) || currentYear)}
                  />
                </FilterPanel.Field>
              </FilterPanel>
            </div>
          </div>

          <Modal
            open={uploadOpen}
            onClose={() => setUploadOpen(false)}
            closeAfterTransition
            slots={{ backdrop }}
            slotProps={{ backdrop: { timeout: 500 } }}
          >
            <Fade in={uploadOpen}>
              <Box style={modalStyle}>
                <h5 className="mb-3">Upload Monthly Expense</h5>
                <form onSubmit={handleUpload}>
                  <div className="row mb-2">
                    <div className="col-6">
                      <label className="form-label">Year</label>
                      <input
                        type="number"
                        className="form-control"
                        value={form.year}
                        min={2020}
                        max={2100}
                        onChange={(e) => setForm({ ...form, year: e.target.value })}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Month (1-12)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={form.month}
                        min={1}
                        max={12}
                        onChange={(e) => setForm({ ...form, month: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Coin (Symbol)</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="BTC, ETH, USDT..."
                      value={form.coin}
                      onChange={(e) => setForm({ ...form, coin: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Total Network Fee (USD)</label>
                    <input
                      type="number"
                      step="any"
                      className="form-control"
                      value={form.totalNetworkFeeUsd}
                      onChange={(e) => setForm({ ...form, totalNetworkFeeUsd: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Notes (optional)</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setUploadOpen(false)}
                      disabled={uploadSubmitting}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={uploadSubmitting}>
                      {uploadSubmitting ? <Spinner size="sm" /> : "Upload"}
                    </button>
                  </div>
                </form>
              </Box>
            </Fade>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default WithdrawalFees;
